import React, { useMemo, useState } from 'react';
import { ICON } from '../../../icons/iconIds';
import IconText from '../../icons/IconText';
import { 
  transformStandardsBiasWorkorderForChart,
  getHeatmapColor,
  COLORS
} from '../../../utils/qaqc/chartDataTransform';
import ChartImageDownloadButton from '../ChartImageDownloadButton';
import PrintStandardsHeatmapChart from '../reports/print/PrintStandardsHeatmapChart';

const StandardsHeatmapChart = ({ 
  data,
  selectedElement = 'all',
  onElementChange,
  onCellClick,
  height = 600
}) => {
  const [metric, setMetric] = useState('meanZ'); // 'meanZ', 'pctWarn', 'pctFail', 'zSlopeVsSequence'
  const [minNCrm, setMinNCrm] = useState(3);
  const [groupBy, setGroupBy] = useState('element'); // 'element' or 'crm'
  
  // Transform data
  const chartData = useMemo(() => {
    return transformStandardsBiasWorkorderForChart(
      data, 
      selectedElement === 'all' ? null : selectedElement,
      minNCrm
    );
  }, [data, selectedElement, minNCrm]);
  
  // Organize data into heatmap structure
  const heatmapData = useMemo(() => {
    if (chartData.length === 0) return { rows: [], columns: [], cells: {} };
    
    const rows = new Set();
    const columns = new Set();
    const cells = {};
    
    chartData.forEach(item => {
      const rowKey = groupBy === 'element' ? item.element : item.crmCode;
      const colKey = item.workorderId;
      
      rows.add(rowKey);
      columns.add(colKey);
      
      const cellKey = `${rowKey}-${colKey}`;
      if (!cells[cellKey]) {
        cells[cellKey] = [];
      }
      cells[cellKey].push(item);
    });
    
    return {
      rows: Array.from(rows).sort(),
      columns: Array.from(columns).sort(),
      cells
    };
  }, [chartData, groupBy]);
  
  // Get value for metric
  const getMetricValue = (item) => {
    switch (metric) {
      case 'meanZ': return item.meanZ;
      case 'pctWarn': return item.pctWarn;
      case 'pctFail': return item.pctFail;
      case 'zSlopeVsSequence': return item.zSlopeVsSequence;
      default: return item.meanZ;
    }
  };
  
  // Get color for metric
  const getCellColor = (value) => {
    if (value === null || value === undefined) return '#1f2937';
    
    if (metric === 'meanZ') {
      return getHeatmapColor(value);
    } else if (metric === 'pctWarn' || metric === 'pctFail') {
      // Percentage-based color scale
      if (value >= 20) return '#991b1b';
      if (value >= 10) return '#ef4444';
      if (value >= 5) return '#f59e0b';
      if (value > 0) return '#fbbf24';
      return '#10b981';
    } else {
      // Slope-based color scale
      const absVal = Math.abs(value);
      if (absVal >= 0.5) return '#991b1b';
      if (absVal >= 0.3) return '#ef4444';
      if (absVal >= 0.1) return '#f59e0b';
      return '#10b981';
    }
  };
  
  // Format metric value for display
  const formatMetricValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (metric === 'pctWarn' || metric === 'pctFail') {
      return `${value.toFixed(1)}%`;
    }
    return value.toFixed(2);
  };
  
  // Handle cell click
  const handleCellClick = (rowKey, colKey) => {
    const cellKey = `${rowKey}-${colKey}`;
    const cellData = heatmapData.cells[cellKey];
    if (cellData && cellData.length > 0 && onCellClick) {
      onCellClick(cellData[0]);
    }
  };
  
  // Get elements for filter
  const elements = useMemo(() => {
    const uniqueElements = new Set();
    data.forEach(item => {
      if (item.element) uniqueElements.add(item.element);
    });
    return Array.from(uniqueElements).sort();
  }, [data]);
  
  return (
    <div className="standards-heatmap-chart">
      {/* Filter Controls */}
      <div style={{ 
        padding: '1rem',
        background: '#111827',
        border: '1px solid #374151',
        borderRadius: '8px',
        marginBottom: '1rem'
      }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Element Filter */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <label style={{ fontSize: '0.875rem', color: '#9ca3af' }}>Element:</label>
            <select
              value={selectedElement}
              onChange={(e) => onElementChange && onElementChange(e.target.value)}
              style={{
                padding: '0.25rem 0.75rem',
                background: '#374151',
                color: '#f9fafb',
                border: '1px solid #4b5563',
                borderRadius: '4px',
                fontSize: '0.875rem'
              }}
            >
              <option value="all">All Elements</option>
              {elements.map(element => (
                <option key={element} value={element}>{element}</option>
              ))}
            </select>
          </div>
          
          {/* Metric Selection */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <label style={{ fontSize: '0.875rem', color: '#9ca3af' }}>Metric:</label>
            <select
              value={metric}
              onChange={(e) => setMetric(e.target.value)}
              style={{
                padding: '0.25rem 0.75rem',
                background: '#374151',
                color: '#f9fafb',
                border: '1px solid #4b5563',
                borderRadius: '4px',
                fontSize: '0.875rem'
              }}
            >
              <option value="meanZ">Mean Z-Score</option>
              <option value="pctWarn">% Warning</option>
              <option value="pctFail">% Fail</option>
              <option value="zSlopeVsSequence">Z-Score Slope</option>
            </select>
          </div>
          
          {/* Group By */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <label style={{ fontSize: '0.875rem', color: '#9ca3af' }}>Group by:</label>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              <button
                onClick={() => setGroupBy('element')}
                style={{
                  padding: '0.25rem 0.75rem',
                  background: groupBy === 'element' ? '#06b6d4' : '#374151',
                  color: groupBy === 'element' ? '#0c4a6e' : '#f9fafb',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  fontWeight: groupBy === 'element' ? '600' : '400',
                  cursor: 'pointer'
                }}
              >
                Element
              </button>
              <button
                onClick={() => setGroupBy('crm')}
                style={{
                  padding: '0.25rem 0.75rem',
                  background: groupBy === 'crm' ? '#06b6d4' : '#374151',
                  color: groupBy === 'crm' ? '#0c4a6e' : '#f9fafb',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  fontWeight: groupBy === 'crm' ? '600' : '400',
                  cursor: 'pointer'
                }}
              >
                CRM Code
              </button>
            </div>
          </div>
          
          {/* Min n_crm Slider */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', minWidth: '200px' }}>
            <label style={{ fontSize: '0.875rem', color: '#9ca3af' }}>Min Samples:</label>
            <input
              type="range"
              min="1"
              max="10"
              value={minNCrm}
              onChange={(e) => setMinNCrm(parseInt(e.target.value))}
              style={{ flex: 1 }}
            />
            <span style={{ fontSize: '0.875rem', color: '#06b6d4', fontWeight: '600', minWidth: '30px' }}>
              {minNCrm}
            </span>
          </div>

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
            <ChartImageDownloadButton
              disabled={!data?.length || heatmapData.rows.length === 0}
              label="Download heatmap (print style)"
              getJobs={() => [
                {
                  Component: PrintStandardsHeatmapChart,
                  props: {
                    data,
                    selectedElement,
                    metric,
                    minNCrm,
                    groupBy
                  },
                  filenameStem: `qaqc_standards_heatmap_${selectedElement}_${metric}_${groupBy}_n${minNCrm}`
                }
              ]}
            />
          </div>
        </div>
      </div>
      
      {/* Legend */}
      <div style={{ 
        padding: '1rem',
        background: '#111827',
        border: '1px solid #374151',
        borderRadius: '8px',
        marginBottom: '1rem'
      }}>
        <div style={{ fontSize: '0.875rem', color: '#9ca3af', marginBottom: '0.5rem' }}>
          Color Scale:
        </div>
        {metric === 'meanZ' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Negative Bias</span>
            <div style={{ display: 'flex', height: '20px', flex: 1 }}>
              <div style={{ flex: 1, background: '#1e40af' }} />
              <div style={{ flex: 1, background: '#3b82f6' }} />
              <div style={{ flex: 1, background: '#60a5fa' }} />
              <div style={{ flex: 1, background: '#93c5fd' }} />
              <div style={{ flex: 1, background: '#f9fafb' }} />
              <div style={{ flex: 1, background: '#fca5a5' }} />
              <div style={{ flex: 1, background: '#f87171' }} />
              <div style={{ flex: 1, background: '#ef4444' }} />
              <div style={{ flex: 1, background: '#991b1b' }} />
            </div>
            <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Positive Bias</span>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Low</span>
            <div style={{ display: 'flex', height: '20px', flex: 1 }}>
              <div style={{ flex: 1, background: '#10b981' }} />
              <div style={{ flex: 1, background: '#fbbf24' }} />
              <div style={{ flex: 1, background: '#f59e0b' }} />
              <div style={{ flex: 1, background: '#ef4444' }} />
              <div style={{ flex: 1, background: '#991b1b' }} />
            </div>
            <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>High</span>
          </div>
        )}
      </div>
      
      {/* Heatmap */}
      {heatmapData.rows.length === 0 || heatmapData.columns.length === 0 ? (
        <div style={{ 
          padding: '3rem',
          textAlign: 'center',
          background: '#111827',
          border: '1px solid #374151',
          borderRadius: '8px',
          color: '#9ca3af'
        }}>
          <p>No data available for the selected filters.</p>
          <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
            Try adjusting the element filter or minimum sample count.
          </p>
        </div>
      ) : (
        <div style={{
          overflowX: 'auto',
          background: '#111827',
          border: '1px solid #374151',
          borderRadius: '8px',
          maxHeight: height
        }}>
          <table style={{
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: '2px',
            fontSize: '0.75rem'
          }}>
            <thead>
              <tr>
                <th style={{
                  position: 'sticky',
                  left: 0,
                  background: '#1f2937',
                  padding: '0.5rem',
                  color: '#9ca3af',
                  fontWeight: '600',
                  textAlign: 'left',
                  zIndex: 20,
                  borderBottom: '2px solid #374151'
                }}>
                  {groupBy === 'element' ? 'Element' : 'CRM Code'}
                </th>
                {heatmapData.columns.map(col => (
                  <th key={col} style={{
                    padding: '0.5rem',
                    color: '#9ca3af',
                    fontWeight: '600',
                    textAlign: 'center',
                    background: '#1f2937',
                    borderBottom: '2px solid #374151',
                    minWidth: '80px',
                    fontSize: '0.7rem'
                  }}>
                    <div style={{ 
                      writingMode: 'vertical-rl',
                      transform: 'rotate(180deg)',
                      whiteSpace: 'nowrap'
                    }}>
                      {col}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {heatmapData.rows.map(row => (
                <tr key={row}>
                  <td style={{
                    position: 'sticky',
                    left: 0,
                    background: '#1f2937',
                    padding: '0.5rem',
                    color: '#f9fafb',
                    fontWeight: '600',
                    borderRight: '2px solid #374151',
                    zIndex: 10
                  }}>
                    {row}
                  </td>
                  {heatmapData.columns.map(col => {
                    const cellKey = `${row}-${col}`;
                    const cellDataArray = heatmapData.cells[cellKey];
                    
                    if (!cellDataArray || cellDataArray.length === 0) {
                      return (
                        <td key={col} style={{
                          padding: '0.5rem',
                          background: '#1f2937',
                          textAlign: 'center',
                          color: '#4b5563'
                        }}>
                          -
                        </td>
                      );
                    }
                    
                    const cellData = cellDataArray[0]; // Use first item if multiple
                    const value = getMetricValue(cellData);
                    const color = getCellColor(value);
                    
                    return (
                      <td 
                        key={col}
                        onClick={() => handleCellClick(row, col)}
                        style={{
                          padding: '0.5rem',
                          background: color,
                          textAlign: 'center',
                          fontWeight: '600',
                          color: value !== null && Math.abs(value) < 0.3 ? '#111827' : '#f9fafb',
                          cursor: onCellClick ? 'pointer' : 'default',
                          transition: 'transform 0.2s',
                          position: 'relative'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.05)';
                          e.currentTarget.style.zIndex = '5';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.zIndex = '1';
                        }}
                        title={`${row} - ${col}\n${metric}: ${formatMetricValue(value)}\nSamples: ${cellData.nCrm}`}
                      >
                        <div>{formatMetricValue(value)}</div>
                        <div style={{ fontSize: '0.65rem', opacity: 0.8 }}>n={cellData.nCrm}</div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Helper text */}
      {onCellClick && heatmapData.rows.length > 0 && (
        <div style={{ 
          marginTop: '1rem',
          padding: '0.75rem',
          background: '#111827',
          border: '1px solid #374151',
          borderRadius: '6px',
          fontSize: '0.875rem', 
          color: '#9ca3af', 
          textAlign: 'center'
        }}>
          <IconText icon={ICON.uiTip} iconSize={16} as="span">Click any cell</IconText> to view detailed drift chart for that workorder and element/CRM combination
        </div>
      )}
    </div>
  );
};

export default StandardsHeatmapChart;
